import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';
import ClientApiV2 from '../client-api-v2';
import SliceRequest from '../slice-request';

export type CompanyDocumentListItem = {
  asset_id: string;
  document_type_code: string;
  name: string;
  extension: string;
  last_updated_date: string;
  status: 'current' | 'outdated';
};

export type OutdatedCompanyDocumentListItem = {
  asset_id: string;
  document_type_code: string;
  document_type_label: string;
  category_code: string;
  category_label: string;
  frequency: 'frequent' | 'conditional';
  status: 'outdated';
};

export type RenamedCompanyDocument = {
  asset_id: string;
  name: string;
};

export type CreatedCompanyDocument = {
  asset_id: string;
  document_type_code: string;
};

export type MoveCompanyDocumentResult = {
  asset_id: string;
  document_type_code: string;
  category_code: string;
};

type CompanyDocumentsState = {
  statusByCategory: Record<
    string,
    'idle' | 'pending' | 'succeeded' | 'failed'
  >;
  itemsByCategory: Record<string, CompanyDocumentListItem[]>;
  outdatedItems: OutdatedCompanyDocumentListItem[];
  outdatedStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
};

const initialState: CompanyDocumentsState = {
  statusByCategory: {},
  itemsByCategory: {},
  outdatedItems: [],
  outdatedStatus: 'idle',
};

const emptyCompanyDocuments: CompanyDocumentListItem[] = [];

export const fetchCompanyDocuments = SliceRequest<
  CompanyDocumentListItem[],
  { api: ClientApiV2; categoryCode: string }
>('fetchCompanyDocuments', async ({ api, categoryCode }) => {
  const response = await api.get<CompanyDocumentListItem[]>(
    '/company-documents',
    { params: { category_code: categoryCode } },
  );
  return response.data;
});

export const fetchOutdatedCompanyDocuments = SliceRequest<
  OutdatedCompanyDocumentListItem[],
  { api: ClientApiV2; locale: string }
>('fetchOutdatedCompanyDocuments', async ({ api, locale }) => {
  const response = await api.get<OutdatedCompanyDocumentListItem[]>(
    '/company-documents',
    { params: { status: 'outdated', locale } },
  );
  return response.data;
});

export const uploadCompanyDocument = SliceRequest<
  CreatedCompanyDocument,
  { api: ClientApiV2; file: File; documentTypeCode: string }
>('uploadCompanyDocument', async ({ api, file, documentTypeCode }) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type_code', documentTypeCode);

  const response = await api.post<CreatedCompanyDocument>(
    '/company-documents',
    formData,
    undefined,
    'multipart/form-data',
  );
  return response.data;
});

export const renameCompanyDocument = SliceRequest<
  RenamedCompanyDocument,
  {
    api: ClientApiV2;
    assetId: string;
    name: string;
    categoryCode: string;
  }
>('renameCompanyDocument', async ({ api, assetId, name }) => {
  const response = await api.patch<RenamedCompanyDocument>(
    `/company-documents/${assetId}/name`,
    { name },
  );
  return response.data;
});

export const deleteCompanyDocument = SliceRequest<
  void,
  { api: ClientApiV2; assetId: string }
>('deleteCompanyDocument', async ({ api, assetId }) => {
  await api.delete(`/company-documents/${assetId}`);
});

export const moveCompanyDocument = SliceRequest<
  MoveCompanyDocumentResult,
  {
    api: ClientApiV2;
    assetId: string;
    documentTypeCode: string;
  }
>(
  'moveCompanyDocument',
  async ({ api, assetId, documentTypeCode }) => {
    const response = await api.patch<MoveCompanyDocumentResult>(
      `/company-documents/${assetId}/type`,
      { document_type_code: documentTypeCode },
    );
    return response.data;
  },
);

const companyDocumentsSlice = createSlice({
  name: 'companyDocuments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchCompanyDocuments.pending, (state, action) => {
      state.statusByCategory[action.meta.arg.categoryCode] = 'pending';
    });
    builder.addCase(fetchCompanyDocuments.fulfilled, (state, action) => {
      const categoryCode = action.meta.arg.categoryCode;
      state.itemsByCategory[categoryCode] = action.payload;
      state.statusByCategory[categoryCode] = 'succeeded';
    });
    builder.addCase(fetchCompanyDocuments.rejected, (state, action) => {
      state.statusByCategory[action.meta.arg.categoryCode] = 'failed';
    });
    builder.addCase(renameCompanyDocument.fulfilled, (state, action) => {
      const documents =
        state.itemsByCategory[action.meta.arg.categoryCode] ?? [];
      const document = documents.find(
        ({ asset_id }) => asset_id === action.payload.asset_id,
      );
      if (document) document.name = action.payload.name;
    });
    builder.addCase(fetchOutdatedCompanyDocuments.pending, (state) => {
      state.outdatedStatus = 'pending';
    });
    builder.addCase(
      fetchOutdatedCompanyDocuments.fulfilled,
      (state, { payload }) => {
        state.outdatedItems = payload;
        state.outdatedStatus = 'succeeded';
      },
    );
    builder.addCase(fetchOutdatedCompanyDocuments.rejected, (state) => {
      state.outdatedStatus = 'failed';
    });
  },
});

export const selectCompanyDocumentsByCategory = (
  state: RootState,
  categoryCode: string | undefined,
) =>
  categoryCode
    ? (state.companyDocuments.itemsByCategory[categoryCode] ??
      emptyCompanyDocuments)
    : emptyCompanyDocuments;

export const selectCompanyDocumentsStatus = (
  state: RootState,
  categoryCode: string | undefined,
) =>
  categoryCode
    ? (state.companyDocuments.statusByCategory[categoryCode] ?? 'idle')
    : 'idle';

export const selectOutdatedCompanyDocuments = (state: RootState) =>
  state.companyDocuments.outdatedItems;

export const selectOutdatedCompanyDocumentsStatus = (state: RootState) =>
  state.companyDocuments.outdatedStatus;

export const companyDocumentsReducer = companyDocumentsSlice.reducer;
